import { Page } from 'components/Page/Page';
import {
  ProductGroupType,
  ProductGroupFormValue,
  AUTO_UPDATE_VALUE
} from 'core/limitation/ProductGroup';
import { GeneralSection } from './GeneralSection';
import { SelectGroupSection } from './SelectGroupSection';
import { FilterSpaceSection } from './FilterSpaceSection';
import { OtherSection } from './OtherSection';
import { Formik } from 'formik';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { useCallback, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { Form } from 'components/common/form/Form';
import { FetchDomain } from './FetchDomain';
import { ProductGroupFormData } from './ProductGroupFormModel';
import { FetchAgency } from './FetchAgency';
import { uniq } from 'lodash';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import { getOutdoorDeliveryUnit } from './utils';

export type ProductGroupFormProps = {
  useModel: () => ProductGroupFormData;
};

export const ProductGroupForm: React.FC<ProductGroupFormProps> = ({
  useModel
}) => {
  const {
    title,
    loading,
    initProductGroup,
    productGroupTypeOptions,
    spaceOptions,
    domainOptions,
    setSpaceOptions,
    setDomainOptions,
    validate,
    save
  } = useModel();

  const [redirectPath, setRedirectPath] = useState<string>();

  const [adFormatAndChannelOptions, setAdFormatAndChannelOptions] = useState<
    SelectOptions[]
  >([]);
  const [agencyOptions, setAgencyOptions] = useState<SelectOptions[]>([]);
  // {"Outdoor-PIC": { "administrativeArea": options, "cityGroup": options... }, "Banner": {...}}
  const [spaceAttributesOptions, setSpaceAttributesOptions] = useState<{
    [key: string]: { [attribute: string]: SelectOptions[] };
  }>({});
  const [currentChannelOutdoorSpaces, setCurrentChannelOutdoorSpaces] =
    useState<SelectOptions[]>([]);

  const onOutdoorSpacesFetched = useCallback(
    (outdoorSpaces: SelectOptions[]) => {
      // remove the prefix (channelGroupId + ',') from label for compare user input
      setCurrentChannelOutdoorSpaces(
        outdoorSpaces.map((space) => ({
          ...space,
          label: space.label.replace(space.value + ',', ''),
          value: space.value
        }))
      );
    },
    []
  );

  const onSubmit = useCallback(
    async (values: ProductGroupFormValue) => {
      if (values.groupType === ProductGroupType.ADSPACE) {
        const hasAutoUpdate = values.groupValues.some(
          (option) => option.value === AUTO_UPDATE_VALUE
        );
        if (values.adFormat === 'Outdoor') {
          const deliveryUnit = getOutdoorDeliveryUnit(
            values.adFormat,
            values.channelGroupId
          );
          const isStoreUnit = deliveryUnit === DeliveryUnit.STORE;
          const isDeviceUnit = deliveryUnit === DeliveryUnit.DEVICE;
          if (!hasAutoUpdate) {
            const attributeKey = isStoreUnit
              ? 'storeNum'
              : isDeviceUnit
                ? 'deviceCharacteristic'
                : undefined;
            if (attributeKey) {
              const selectedValues = uniq(
                [...values.groupValues, ...values.customGroupValues].map(
                  (opt) => opt.value.toString()
                )
              );
              values.attributes = {
                ...values.attributes,
                [attributeKey]: selectedValues
              };
            }
          }
          if (isStoreUnit || isDeviceUnit) {
            values.groupValues = [];
            values.customGroupValues = [];
          }
        }
      }
      await save(values);
      setRedirectPath('/system/product-groups');
    },
    [save]
  );

  const renderFormBtns = useCallback(
    () => (
      <div>
        <Form.SubmitButton />
        <Form.CancelButton />
      </div>
    ),
    []
  );

  if (!initProductGroup) {
    return <LoadingIndicator />;
  }

  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  return (
    <Page title={title} minWidth={1000}>
      {loading && <LoadingIndicator />}
      <Formik
        initialValues={initProductGroup}
        validate={validate}
        onSubmit={onSubmit}
        validateOnBlur={false}
      >
        {(formikProps) => {
          const { values } = formikProps;
          const productGroupType = values.groupType;
          const isSpaceGroup = productGroupType === ProductGroupType.ADSPACE;
          const disable =
            (values.groupValues && values.groupValues.length > 0) ||
            (values.customGroupValues && values.customGroupValues.length > 0);
          const adFormat = isSpaceGroup
            ? values.adFormat === ''
              ? undefined
              : values.adFormat
            : undefined;
          const channelGroupId = isSpaceGroup
            ? values.channelGroupId
            : undefined;
          return (
            <Form formikProps={formikProps} renderFormBtns={renderFormBtns}>
              <GeneralSection
                productGroupTypeOptions={productGroupTypeOptions}
              />
              {isSpaceGroup ? (
                <FilterSpaceSection
                  disable={disable}
                  onSpacesOptionsFetched={setSpaceOptions}
                  adFormatAndChannelOptions={adFormatAndChannelOptions}
                  setAdFormatAndChannelOptions={setAdFormatAndChannelOptions}
                  spaceAttributes={spaceAttributesOptions}
                  setSpaceAttributes={setSpaceAttributesOptions}
                  onOutdoorSpacesFetched={onOutdoorSpacesFetched}
                />
              ) : (
                <FetchDomain
                  needFetch={domainOptions.length === 0}
                  onDomainOptionsFetched={setDomainOptions}
                />
              )}
              <FetchAgency
                adFormat={adFormat}
                channelGroupId={channelGroupId}
                onAgencyOptionsFetched={setAgencyOptions}
              />
              <SelectGroupSection
                options={isSpaceGroup ? spaceOptions : domainOptions}
                outdoorOptions={currentChannelOutdoorSpaces}
              />
              <OtherSection agencyOptions={agencyOptions} />
            </Form>
          );
        }}
      </Formik>
    </Page>
  );
};
